import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchReportedCommentsAPI,
  fetchCommentReportsAPI,
  hideCommentAPI,
  deleteCommentAPI,
  dismissReportsAPI,
  fetchPreviousReportsAPI,
  restoreDeletedCommentAPI,
} from "./tournamentCommentReportsAPI";
import { showModal } from "../statusModal/statusModal";

const initialState = {
  reportedComments: [],
  previousReports: [], // For previous reports page
  selectedComment: null,
  commentReports: [],
  tournaments: [], // Grouped tournaments with report counts
  currentPage: 1,
  previousReportsPage: 1,
  totalPages: 1,
  hasMore: true,
  previousReportsHasMore: true,
  status: "idle",
  fetchReportsStatus: "idle",
  fetchPreviousReportsStatus: "idle",
  actionStatus: "idle",
  selectedTournamentId: null, // null means "All Tournaments"
  selectedStatusFilter: null, // "resolved" | "dismissed" | null (both)
  selectedActionFilter: null, // "hidden" | "deleted" | "dismissed" | null (all)
  error: null,
};

export const fetchReportedComments = createAsyncThunk(
  "tournamentCommentReports/fetchReportedComments",
  async (
    { status = "open", tournamentId = null, page = 1, limit = 20 },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const data = await fetchReportedCommentsAPI({
        status,
        tournamentId,
        page,
        limit,
      });

      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء جلب تقارير التعليقات",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch reported comments",
      );
    }
  },
);

export const fetchCommentReports = createAsyncThunk(
  "tournamentCommentReports/fetchCommentReports",
  async (commentId, { dispatch, rejectWithValue }) => {
    try {
      const data = await fetchCommentReportsAPI(commentId);
      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء جلب تفاصيل التقارير",
          status: "error",
        }),
      );
      console.log(error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch comment reports",
      );
    }
  },
);

export const hideComment = createAsyncThunk(
  "tournamentCommentReports/hideComment",
  async (
    { commentId, isHidden, hideReason },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const data = await hideCommentAPI(commentId, isHidden, hideReason);
      dispatch(
        showModal({
          message: isHidden
            ? "تم إخفاء التعليق بنجاح"
            : "تم إظهار التعليق بنجاح",
          status: "success",
        }),
      );
      return { commentId, data };
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء إخفاء التعليق",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to hide comment",
      );
    }
  },
);

export const deleteComment = createAsyncThunk(
  "tournamentCommentReports/deleteComment",
  async (commentId, { dispatch, rejectWithValue }) => {
    try {
      const data = await deleteCommentAPI(commentId);
      dispatch(
        showModal({
          message: "تم حذف التعليق بنجاح",
          status: "success",
        }),
      );
      return { commentId, data };
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء حذف التعليق",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete comment",
      );
    }
  },
);

export const dismissReports = createAsyncThunk(
  "tournamentCommentReports/dismissReports",
  async ({ commentId, adminNote }, { dispatch, rejectWithValue }) => {
    try {
      const data = await dismissReportsAPI(commentId, adminNote);
      dispatch(
        showModal({
          message: "تم رفض التقارير بنجاح",
          status: "success",
        }),
      );
      return { commentId, data };
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء رفض التقارير",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to dismiss reports",
      );
    }
  },
);

export const fetchPreviousReports = createAsyncThunk(
  "tournamentCommentReports/fetchPreviousReports",
  async (
    { status, tournamentId, actionTaken, page = 1, limit = 20 },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const data = await fetchPreviousReportsAPI({
        status,
        tournamentId,
        actionTaken,
        page,
        limit,
      });
      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء جلب التقارير السابقة",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch previous reports",
      );
    }
  },
);

export const restoreDeletedComment = createAsyncThunk(
  "tournamentCommentReports/restoreDeletedComment",
  async (commentId, { dispatch, rejectWithValue }) => {
    try {
      const data = await restoreDeletedCommentAPI(commentId);
      dispatch(
        showModal({
          message: "تم استعادة التعليق بنجاح",
          status: "success",
        }),
      );
      return { commentId, data };
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء استعادة التعليق",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to restore comment",
      );
    }
  },
);

const tournamentCommentReportsSlice = createSlice({
  name: "tournamentCommentReports",
  initialState,
  reducers: {
    setSelectedComment: (state, action) => {
      state.selectedComment = action.payload;
      state.commentReports = [];
    },
    setSelectedTournament: (state, action) => {
      state.selectedTournamentId = action.payload;
      state.reportedComments = [];
      state.currentPage = 1;
      state.hasMore = true;
      state.selectedComment = null;
      state.commentReports = [];
    },
    setStatusFilter: (state, action) => {
      state.selectedStatusFilter = action.payload;
      state.previousReports = [];
      state.previousReportsPage = 1;
      state.previousReportsHasMore = true;
      state.selectedComment = null;
      state.commentReports = [];
    },
    setActionFilter: (state, action) => {
      state.selectedActionFilter = action.payload;
      state.previousReports = [];
      state.previousReportsPage = 1;
      state.previousReportsHasMore = true;
      state.selectedComment = null;
      state.commentReports = [];
    },
    clearSelectedComment: (state) => {
      state.selectedComment = null;
      state.commentReports = [];
    },
    resetState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch reported comments
      .addCase(fetchReportedComments.pending, (state) => {
        state.fetchReportsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchReportedComments.fulfilled, (state, action) => {
        state.fetchReportsStatus = "succeeded";
        const { items, page, limit } = action.payload;
        const totalItems = items.length;
        const totalPages = Math.ceil(totalItems / limit);

        if (page === 1) {
          state.reportedComments = items;
        } else {
          state.reportedComments = [...state.reportedComments, ...items];
        }

        state.currentPage = page;
        state.totalPages = totalPages;
        state.hasMore = items.length === limit;

        // Group by tournament
        const tournamentMap = new Map();
        state.reportedComments.forEach((item) => {
          const tournamentId =
            item.comment?.tournamentId || item.tournament?._id;
          if (tournamentId) {
            const tournamentIdStr = String(tournamentId);
            if (!tournamentMap.has(tournamentIdStr)) {
              // Use tournament name from API response (tournament.name or comment.tournamentName)
              const tournamentName =
                item.tournament?.name ||
                item.comment?.tournamentName ||
                `بطولة ${tournamentIdStr.slice(0, 8)}...`;
              tournamentMap.set(tournamentIdStr, {
                tournamentId: tournamentIdStr,
                name: tournamentName,
                reportsCount: 0,
              });
            }
            tournamentMap.get(tournamentIdStr).reportsCount +=
              item.reportsCount || 1;
          }
        });

        state.tournaments = Array.from(tournamentMap.values()).sort(
          (a, b) => b.reportsCount - a.reportsCount,
        );
      })
      .addCase(fetchReportedComments.rejected, (state, action) => {
        state.fetchReportsStatus = "failed";
        state.error = action.payload;
      })
      // Fetch comment reports
      .addCase(fetchCommentReports.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCommentReports.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.commentReports = action.payload.reports || [];
      })
      .addCase(fetchCommentReports.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Hide comment
      .addCase(hideComment.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        const { commentId } = action.payload;
        const comment = state.reportedComments.find(
          (c) => c.commentId === commentId || c.comment?._id === commentId,
        );
        if (comment && comment.comment) {
          comment.comment.isHidden = true;
        }
        if (state.selectedComment?.comment?._id === commentId) {
          state.selectedComment.comment.isHidden = true;
        }
      })
      .addCase(hideComment.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.error = action.payload;
      })
      // Delete comment
      .addCase(deleteComment.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        const { commentId } = action.payload;
        state.reportedComments = state.reportedComments.filter((c) => {
          const cId = c.commentId || c.comment?._id;
          return String(cId) !== String(commentId);
        });
        // Recalculate tournaments after deletion
        const tournamentMap = new Map();
        state.reportedComments.forEach((item) => {
          const tournamentId =
            item.comment?.tournamentId || item.tournament?._id;
          if (tournamentId) {
            const tournamentIdStr = String(tournamentId);
            if (!tournamentMap.has(tournamentIdStr)) {
              // Use tournament name from API response
              const tournamentName =
                item.tournament?.name ||
                item.comment?.tournamentName ||
                `بطولة ${tournamentIdStr.slice(0, 8)}...`;
              tournamentMap.set(tournamentIdStr, {
                tournamentId: tournamentIdStr,
                name: tournamentName,
                reportsCount: 0,
              });
            }
            tournamentMap.get(tournamentIdStr).reportsCount +=
              item.reportsCount || 1;
          }
        });
        state.tournaments = Array.from(tournamentMap.values()).sort(
          (a, b) => b.reportsCount - a.reportsCount,
        );
        if (
          state.selectedComment &&
          (state.selectedComment.commentId === commentId ||
            state.selectedComment.comment?._id === commentId)
        ) {
          state.selectedComment = null;
          state.commentReports = [];
        }
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.error = action.payload;
      })
      // Dismiss reports
      .addCase(dismissReports.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(dismissReports.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        const { commentId } = action.payload;
        state.reportedComments = state.reportedComments.filter((c) => {
          const cId = c.commentId || c.comment?._id;
          return String(cId) !== String(commentId);
        });
        // Recalculate tournaments after dismissal
        const tournamentMap = new Map();
        state.reportedComments.forEach((item) => {
          const tournamentId =
            item.comment?.tournamentId || item.tournament?._id;
          if (tournamentId) {
            const tournamentIdStr = String(tournamentId);
            if (!tournamentMap.has(tournamentIdStr)) {
              // Use tournament name from API response
              const tournamentName =
                item.tournament?.name ||
                item.comment?.tournamentName ||
                `بطولة ${tournamentIdStr.slice(0, 8)}...`;
              tournamentMap.set(tournamentIdStr, {
                tournamentId: tournamentIdStr,
                name: tournamentName,
                reportsCount: 0,
              });
            }
            tournamentMap.get(tournamentIdStr).reportsCount +=
              item.reportsCount || 1;
          }
        });
        state.tournaments = Array.from(tournamentMap.values()).sort(
          (a, b) => b.reportsCount - a.reportsCount,
        );
        if (
          state.selectedComment &&
          (state.selectedComment.commentId === commentId ||
            state.selectedComment.comment?._id === commentId)
        ) {
          state.selectedComment = null;
          state.commentReports = [];
        }
      })
      .addCase(dismissReports.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.error = action.payload;
      })
      // Fetch previous reports
      .addCase(fetchPreviousReports.pending, (state) => {
        state.fetchPreviousReportsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchPreviousReports.fulfilled, (state, action) => {
        state.fetchPreviousReportsStatus = "succeeded";
        const { items, page, limit } = action.payload;
        const totalItems = items.length;
        const totalPages = Math.ceil(totalItems / limit);

        if (page === 1) {
          state.previousReports = items;
        } else {
          state.previousReports = [...state.previousReports, ...items];
        }

        state.previousReportsPage = page;
        state.totalPages = totalPages;
        state.previousReportsHasMore = items.length === limit;

        // Group by tournament
        const tournamentMap = new Map();
        state.previousReports.forEach((item) => {
          const tournamentId =
            item.comment?.tournamentId || item.tournament?._id;
          if (tournamentId) {
            const tournamentIdStr = String(tournamentId);
            if (!tournamentMap.has(tournamentIdStr)) {
              const tournamentName =
                item.tournament?.name ||
                item.comment?.tournamentName ||
                `بطولة ${tournamentIdStr.slice(0, 8)}...`;
              tournamentMap.set(tournamentIdStr, {
                tournamentId: tournamentIdStr,
                name: tournamentName,
                reportsCount: 0,
              });
            }
            tournamentMap.get(tournamentIdStr).reportsCount +=
              item.reportsCount || 1;
          }
        });

        state.tournaments = Array.from(tournamentMap.values()).sort(
          (a, b) => b.reportsCount - a.reportsCount,
        );
      })
      .addCase(fetchPreviousReports.rejected, (state, action) => {
        state.fetchPreviousReportsStatus = "failed";
        state.error = action.payload;
      })
      // Restore deleted comment
      .addCase(restoreDeletedComment.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(restoreDeletedComment.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        const { commentId } = action.payload;
        // Remove from previous reports list
        state.previousReports = state.previousReports.filter((c) => {
          const cId = c.commentId || c.comment?._id;
          return String(cId) !== String(commentId);
        });
        // Recalculate tournaments
        const tournamentMap = new Map();
        state.previousReports.forEach((item) => {
          const tournamentId =
            item.comment?.tournamentId || item.tournament?._id;
          if (tournamentId) {
            const tournamentIdStr = String(tournamentId);
            if (!tournamentMap.has(tournamentIdStr)) {
              const tournamentName =
                item.tournament?.name ||
                item.comment?.tournamentName ||
                `بطولة ${tournamentIdStr.slice(0, 8)}...`;
              tournamentMap.set(tournamentIdStr, {
                tournamentId: tournamentIdStr,
                name: tournamentName,
                reportsCount: 0,
              });
            }
            tournamentMap.get(tournamentIdStr).reportsCount +=
              item.reportsCount || 1;
          }
        });
        state.tournaments = Array.from(tournamentMap.values()).sort(
          (a, b) => b.reportsCount - a.reportsCount,
        );
        if (
          state.selectedComment &&
          (state.selectedComment.commentId === commentId ||
            state.selectedComment.comment?._id === commentId)
        ) {
          state.selectedComment = null;
          state.commentReports = [];
        }
      })
      .addCase(restoreDeletedComment.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedComment,
  setSelectedTournament,
  setStatusFilter,
  setActionFilter,
  clearSelectedComment,
  resetState,
} = tournamentCommentReportsSlice.actions;

export default tournamentCommentReportsSlice.reducer;
